import React, {useEffect, useState} from 'react';
import {Dropdown, Form} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import TableWithActionsDropdown, {Column} from '../shared/components/TableWithActionsDropdown';
import CheckPaymentModal from '../components/truck-billing/CheckPaymentModal';
import {truckBillingService} from '../services/truckBillingService';

const STATUS_OPTIONS = ['TOODOO', 'All'];

const TruckBillingPage: React.FC = () => {
    const navigate = useNavigate();
    const today = new Date().toISOString().split('T')[0];

    const [selectedDate, setSelectedDate] = useState(today);
    const [truckNumber, setTruckNumber] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [jobData, setJobData] = useState<any[]>([]);
    const [showCheckModal, setShowCheckModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState<any>(null);
    const [lastDepositSlip, setLastDepositSlip] = useState('');

    const onSearch = () => {
        truckBillingService
            .searchJobs(selectedDate, truckNumber, statusFilter === 'TOODOO' ? 'toodoo' : 'all')
            .then(r => setJobData(r ?? []));
    };

    useEffect(() => {
        onSearch();
    }, []);

    const handleWebNotRun = (job: any) => {
        truckBillingService.postWebNotRun(job.jobId).then(onSearch);
    };

    const handleNoPaid = (job: any) => {
        truckBillingService.postNoPaid(job.jobId).then(onSearch);
    };

    const renderActions = (job: any) => {
        if (job.hasPaymentMethod) {
            return (
                <button
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => { e.stopPropagation(); handleWebNotRun(job); }}
                >
                    Web Not Run
                </button>
            );
        }
        return (
            <span className="d-flex gap-1">
                <button
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => { e.stopPropagation(); handleNoPaid(job); }}
                >
                    No Paid
                </button>
                <button
                    className="btn btn-sm btn-success"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedJob(job);
                        setShowCheckModal(true);
                    }}
                >
                    Check
                </button>
            </span>
        );
    };

    const columns: Column<any>[] = [
        {
            label: 'Last Name',
            varName: 'lastName',
            render: (a) => (
                <span
                    className="text-primary"
                    style={{cursor: 'pointer'}}
                    onClick={() => navigate('/admin/customer-details', {state: {customerId: a.customerId}})}
                >
                    {a.lastName}
                </span>
            ),
        },
        {
            label: 'Address',
            varName: 'propertyAddress',
            render: (a) => (
                <span
                    className="text-primary"
                    style={{cursor: 'pointer'}}
                    onClick={() => navigate('/admin/customer-details', {state: {customerId: a.customerId}})}
                >
                    {a.propertyAddress}
                </span>
            ),
        },
        {label: 'Truck #', varName: 'truckNumber'},
        {
            label: 'Arrival',
            varName: 'arrivalDateAndTime',
            render: (a) => a.arrivalDateAndTime
                ? new Date(a.arrivalDateAndTime).toLocaleString('en-US', {
                    month: '2-digit', day: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                })
                : '—',
        },
        {label: 'Status', varName: 'jobStatusName'},
        {
            label: 'Amount',
            varName: 'totalJobPrice',
            render: (a) => (
                <span
                    className="text-primary"
                    style={{cursor: 'pointer'}}
                    onClick={() => navigate('/admin/job-details', {state: {customerId: a.customerId, jobId: a.jobId}})}
                >
                    {a.totalJobPrice != null ? `$${Number(a.totalJobPrice).toFixed(2)}` : '—'}
                </span>
            ),
        },
        {
            label: '',
            varName: 'jobId',
            sortable: false,
            render: renderActions,
        },
    ];

    return (
        <>
            <div className="container-fluid ptCustom">
                <div className="row">
                    <div className="col-12 my-2">
                        <div className="card shadow-sm rounded-lg">
                            <div className="card-body p-4">
                                <div className="row gx-2 align-items-end mb-3">
                                    <div className="col-auto">
                                        <label className="form-label mb-1">Date</label>
                                        <Form.Control
                                            type="date"
                                            value={selectedDate}
                                            onChange={(e) => setSelectedDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-auto">
                                        <label className="form-label mb-1">Truck #</label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Truck #"
                                            value={truckNumber}
                                            onChange={(e) => setTruckNumber(e.target.value)}
                                            style={{width: '120px'}}
                                        />
                                    </div>
                                    <div className="col-auto">
                                        <label className="form-label mb-1">Status</label>
                                        <Dropdown>
                                            <Dropdown.Toggle id="dropdownMenuButtonTruckBilling"
                                                             className="btn btn-white dropdown-toggle w-100">
                                                {statusFilter}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                {STATUS_OPTIONS.map(opt => (
                                                    <Dropdown.Item
                                                        key={opt}
                                                        active={statusFilter === opt}
                                                        onClick={() => setStatusFilter(opt)}
                                                    >
                                                        {opt}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                    <div className="col-auto">
                                        <button className="btn btn-primary" onClick={onSearch}>
                                            Search
                                        </button>
                                    </div>
                                </div>

                                <div className="card no-shadow">
                                    <TableWithActionsDropdown
                                        data={jobData}
                                        columns={columns}
                                        pageSize={50}
                                        defaultSortField="truckNumber"
                                        defaultSortDesc={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CheckPaymentModal
                show={showCheckModal}
                toggle={() => setShowCheckModal(false)}
                jobId={selectedJob?.jobId ?? null}
                defaultAmount={selectedJob?.totalJobPrice ?? null}
                defaultDepositSlip={lastDepositSlip}
                onDepositSlipSaved={setLastDepositSlip}
                refreshData={onSearch}
            />
        </>
    );
};

export default TruckBillingPage;
